/**
 * Transliteration Ranking Page
 * Main entry point for evaluating IPA transliteration candidates
 */

import React, { useEffect, useState } from 'react'
import {
  useContainerWidth,
  LayoutBand,
  H1,
  Separator,
  Row,
  Button,
  useOverlay,
  Paragraph,
  IconButton,
  Cell,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { baseUrl } from '@fs/zion-config'
import { i18n } from '@fs/zion-locale'
import { useUser } from '@fs/zion-user'
import { Arrow } from '@fs/zion-icon'
import AuthoritiesAppPageHead from '../../AuthoritiesAppPageHead'
import { TransliterationEvalProvider, useTransliterationEval } from './TransliterationEvalProvider'
import { FileUploadDialog } from './components/FileUploadDialog/FileUploadDialog'
import { NameEvaluationCard } from './components/NameEvaluationCard/NameEvaluationCard'
import { ProgressIndicator } from './components/ProgressIndicator/ProgressIndicator'
import { MarkdownInstructionsOverlay } from './components/InstructionsOverlay/MarkdownInstructionsOverlay'
import { downloadJSON } from './utils/jsonExporter'
import type { Candidate } from './types'

const ForwardArrow = (): JSX.Element => <Arrow direction="forward" size="sm" />
const BackwardArrow = (): JSX.Element => <Arrow direction="backward" size="sm" />

const TransliterationEvalPageInner: React.FC = () => {
  const [t] = useTranslation()
  const uploadOverlay = useOverlay()
  const instructionsOverlay = useOverlay()
  const user = useUser()
  const {
    uploadedData,
    currentIndex,
    currentName,
    totalNames,
    evaluations,
    saveEvaluation,
    moveToNext,
    moveToPrevious,
    loadSavedState,
  } = useTransliterationEval()

  const atWidth = useContainerWidth()
  const isNarrow = atWidth({ default: true, md: false })

  const [reorderedCandidates, setReorderedCandidates] = useState<Candidate[] | null>(null)
  const [hasFinished, setHasFinished] = useState(false)

  // Try to load saved state on mount
  useEffect(() => {
    loadSavedState()
      .then((loaded) => {
        if (loaded) {
          // Could show a toast notification here
          // eslint-disable-next-line no-console
          console.log('Loaded saved evaluation state from session storage')
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('Failed to load saved state:', err)
      })
  }, [loadSavedState])

  // Reset reordered candidates and finish state when name changes
  useEffect(() => {
    setReorderedCandidates(null)
    setHasFinished(false)
  }, [currentName?.id])

  const handleCandidatesReorder = (candidates: Candidate[]): void => {
    setReorderedCandidates(candidates)
    // Save evaluation immediately when candidates are reordered
    if (currentName) {
      saveEvaluation(currentName.id, candidates, false)
    }
  }

  const handleNext = (): void => {
    if (currentName) {
      if (reorderedCandidates === null) {
        // If user didn't reorder anything, save the original order as evaluated
        saveEvaluation(currentName.id, currentName.candidates, true) // Skip storage save
      }
    }

    setReorderedCandidates(null)
    moveToNext(false) // Do the storage save here with navigation position
  }

  const handleFinish = (): void => {
    // Save the last item evaluation if not already saved
    if (currentName) {
      if (reorderedCandidates === null) {
        saveEvaluation(currentName.id, currentName.candidates, false)
      }
    }
    setReorderedCandidates(null)
    setHasFinished(true)
  }

  const handleDownload = (): void => {
    if (uploadedData) {
      const evaluatorEmail = user?.email || 'unknown@familysearch.org'
      try {
        downloadJSON(uploadedData, evaluations, evaluatorEmail)
        // eslint-disable-next-line no-console
        console.log('Results downloaded successfully')
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Download failed:', err)
      }
    }
  }

  const handlePrevious = (): void => {
    setReorderedCandidates(null)
    setHasFinished(false) // Reset finish state when navigating away
    moveToPrevious()
  }

  // Empty state - no data uploaded
  if (!uploadedData || uploadedData.length === 0) {
    return (
      <LayoutBand>
        <Separator size="xxl" />
        <Row alignX="center">
          <Paragraph size="sm">
            {t(
              'transliteration-eval-page.transliteration-eval-page-inner.empty-state.message',
              'Upload a file to begin evaluating transliteration candidates.'
            )}
          </Paragraph>
        </Row>
        <Row alignX="center">
          <Row gutters="sm">
            <Button onClick={uploadOverlay.handleClick} emphasis="high">
              {t('transliteration-eval-page.transliteration-eval-page-inner.upload-file.button', 'Upload File')}
            </Button>
            <Button onClick={instructionsOverlay.handleClick} emphasis="low">
              {t('transliteration-eval-page.transliteration-eval-page-inner.instructions.button', 'Instructions')}
            </Button>
          </Row>
        </Row>
        {uploadOverlay.isOpen && <FileUploadDialog overlay={uploadOverlay} />}
        {instructionsOverlay.isOpen && <MarkdownInstructionsOverlay overlay={instructionsOverlay} />}
      </LayoutBand>
    )
  }

  return (
    <LayoutBand>
      {/* Main evaluation card with navigation */}
      {currentName && (
        <NameEvaluationCard
          name={currentName}
          onCandidatesReorder={handleCandidatesReorder}
          navigationFooter={
            <Row gutters="sm" alignItemsY="middle" wrap>
              {isNarrow ? (
                <IconButton
                  Icon={BackwardArrow}
                  title={t('transliteration-eval-page.transliteration-eval-page-inner.previous.button', 'Previous')}
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  emphasis="lightHigh"
                />
              ) : (
                <Button onClick={handlePrevious} disabled={currentIndex === 0} emphasis="low">
                  ← {t('transliteration-eval-page.transliteration-eval-page-inner.previous.button', 'Previous')}
                </Button>
              )}
              {isNarrow ? (
                <Cell>
                  <Paragraph size="sm">
                    {currentIndex + 1}/{totalNames}
                  </Paragraph>
                </Cell>
              ) : (
                <ProgressIndicator total={totalNames} currentPosition={currentIndex + 1} />
              )}
              {(() => {
                // On last item - show Finish or Download button (always text, never IconButton)
                if (currentIndex >= totalNames - 1) {
                  if (hasFinished) {
                    return (
                      // key forces a remount so the Download button doesn't inherit the
                      // Finish button's click-debounce state (Button debounces 300ms)
                      <Button key="download" onClick={handleDownload} emphasis="high">
                        {t('transliteration-eval-page.transliteration-eval-page-inner.download.button', 'Download')}
                      </Button>
                    )
                  }
                  return (
                    <Button key="finish" onClick={handleFinish} emphasis="high">
                      {t('transliteration-eval-page.transliteration-eval-page-inner.finish.button', 'Finish')}
                    </Button>
                  )
                }

                // Not on last item - show Next button (IconButton on narrow, text on wide)
                if (isNarrow) {
                  return (
                    <IconButton
                      Icon={ForwardArrow}
                      title={t('transliteration-eval-page.transliteration-eval-page-inner.next.button', 'Next')}
                      onClick={handleNext}
                      emphasis="high"
                    />
                  )
                }

                return (
                  <Button onClick={handleNext} emphasis="high">
                    {`${t('transliteration-eval-page.transliteration-eval-page-inner.next.button', 'Next')} →`}
                  </Button>
                )
              })()}
            </Row>
          }
        />
      )}

      {/* Actions below card */}
      <Row alignX="center">
        <Row gutters="sm">
          <Button onClick={uploadOverlay.handleClick} emphasis="low">
            {t('transliteration-eval-page.transliteration-eval-page-inner.load-new-batch.button', 'Load New Batch')}
          </Button>
          <Button onClick={instructionsOverlay.handleClick} emphasis="low">
            {t('transliteration-eval-page.transliteration-eval-page-inner.instructions.button', 'Instructions')}
          </Button>
        </Row>
      </Row>

      {uploadOverlay.isOpen && <FileUploadDialog overlay={uploadOverlay} />}
      {instructionsOverlay.isOpen && <MarkdownInstructionsOverlay overlay={instructionsOverlay} />}
    </LayoutBand>
  )
}

export default function TransliterationEvalPage(): JSX.Element {
  usePageView({ page_detail: 'TransliterationEval' })
  const [t] = useTranslation()

  return (
    <LayoutBand>
      <AuthoritiesAppPageHead
        title={t('transliteration-eval-page.transliteration-eval-page.title', 'Transliteration Ranking')}
        description={t(
          'transliteration-eval-page.transliteration-eval-page.description',
          'Evaluate and reorder IPA transliteration candidates'
        )}
        canonicalUrl={`${baseUrl}/${i18n.language}/standards/transliteration-eval/`}
      />
      <LayoutBand maxWidth="xxl">
        <Separator />
        <H1 centered size="H3">
          {t('transliteration-eval-page.transliteration-eval-page.title', 'Transliteration Ranking')}
        </H1>
        <Separator size="sm" />

        <TransliterationEvalProvider>
          <TransliterationEvalPageInner />
        </TransliterationEvalProvider>
      </LayoutBand>
    </LayoutBand>
  )
}
